<template>
  <div v-if="campaign" class="portal-bonus-campaign-page">
    <Menu />
    <label
      v-if="campaignSlug === BONUS_CAMPAIGN_SLUG_UHRD"
      class="portal-bonus-campaign-page__mock-toggle"
    >
      <input v-model="mockUhrdGoalAchieved" type="checkbox" />
      <span>{{ $t("portalHome.bonusUhrd.mockGoalAchievedToggle") }}</span>
    </label>
    <label
      v-if="campaignSlug === BONUS_CAMPAIGN_SLUG_AWANS"
      class="portal-bonus-campaign-page__mock-toggle"
    >
      <input v-model="mockAwansFirstMilestoneAchieved" type="checkbox" />
      <span>{{ $t("portalHome.bonusAwans.mockFirstMilestoneAchievedToggle") }}</span>
    </label>
    <div class="portal-bonus-campaign-page__container">
      <div class="portal-bonus-campaign-page__top">
        <div class="portal-bonus-campaign-page__sidebar-top">
          <AccountStatusCard :account="campaign.account" />
          <PurchaseSummaryCard :purchases="campaign.account.purchases" />
        </div>

        <div class="portal-bonus-campaign-page__hero-slot">
          <BonusCampaignHeroBanner
            stretch-to-grid
            :hero-src="campaign.heroSrc"
            :headline-key="`${pageI18nPrefix}.heroHeadline`"
          />
        </div>

        <div class="portal-bonus-campaign-page__sidebar-bottom">
          <BonusCampaignGoalReminderWidget
            :messages-prefix="pageI18nPrefix"
            :data="goalReminderData"
            :icon-src="goalReminderIconSrc"
          />
        </div>

        <div class="portal-bonus-campaign-page__chart">
          <BonusAwansWidget
            v-if="campaignSlug !== BONUS_CAMPAIGN_SLUG_UHRD"
            variant="subpage"
            :title-key="chartTitleKey"
            :data="awansWidgetData"
            :period-label-key="`${pageI18nPrefix}.bonusPeriodLine`"
          />
          <BonusUhidWidget
            v-else
            variant="subpage"
            :data="uhrdWidgetData"
            :period-label-key="`${pageI18nPrefix}.bonusPeriodLine`"
          />
        </div>
      </div>

      <BonusCampaignRulesRow
        class="portal-bonus-campaign-page__rules"
        :messages-prefix="pageI18nPrefix"
      />
      <PortalAppsSection />
    </div>

    <PortalFooter
      :links="footerBundle.footerLinks"
      :socials="footerBundle.footerSocials"
      :logo-src="footerBundle.footerLogoSrc"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from "vue";

import Menu from "@/components/Layout/Menu.vue";
import AccountStatusCard from "@/components/PortalHome/AccountStatusCard.vue";
import BonusCampaignHeroBanner from "@/components/PortalHome/BonusCampaignHeroBanner.vue";
import BonusAwansWidget from "@/components/PortalHome/BonusAwansWidget.vue";
import BonusUhidWidget from "@/components/PortalHome/BonusUhidWidget.vue";
import BonusCampaignGoalReminderWidget from "@/components/PortalHome/BonusCampaignGoalReminderWidget.vue";
import BonusCampaignRulesRow from "@/components/PortalHome/BonusCampaignRulesRow.vue";
import PurchaseSummaryCard from "@/components/PortalHome/PurchaseSummaryCard.vue";
import PortalAppsSection from "@/components/PortalHome/PortalAppsSection.vue";
import PortalFooter from "@/components/PortalHome/PortalFooter.vue";
import {
  toBonusAwansWidgetData,
  toBonusUhrdWidgetData,
  toGoalReminder,
} from "@/data/portal_bonus_campaign_mappers";
import {
  BONUS_CAMPAIGN_SLUG_AWANS,
  BONUS_CAMPAIGN_SLUG_GOALS,
  BONUS_CAMPAIGN_SLUG_UHRD,
  getPortalBonusCampaignPageData,
  portalBonusCampaignFooter,
} from "@/data/portal_bonus_campaign_page.js";
import { portalBonusCampaignsApiMock } from "@/data/portal_bonus_campaigns_mock";
import { portalHomeIcons } from "@/components/PortalHome/portalHomeAssets.js";

const props = defineProps({
  campaignSlug: {
    type: String,
    required: true,
    validator: (v) =>
      v === BONUS_CAMPAIGN_SLUG_UHRD ||
      v === BONUS_CAMPAIGN_SLUG_GOALS ||
      v === BONUS_CAMPAIGN_SLUG_AWANS,
  },
});

const campaign = computed(() => getPortalBonusCampaignPageData(props.campaignSlug));

const goalReminderIconSrc = computed(() => {
  const key = campaign.value?.toolbarIconKey;
  return key ? portalHomeIcons[key] ?? null : null;
});

const mockUhrdGoalAchieved = ref(false);
const mockAwansFirstMilestoneAchieved = ref(false);

const awansProgramMock = computed(() =>
  mockAwansFirstMilestoneAchieved.value
    ? portalBonusCampaignsApiMock.programs.awansFirstMilestoneAchieved
    : portalBonusCampaignsApiMock.programs.awans
);

const goalReminderData = computed(() => {
  if (!campaign.value) return null;
  if (
    props.campaignSlug === BONUS_CAMPAIGN_SLUG_UHRD &&
    mockUhrdGoalAchieved.value
  ) {
    return toGoalReminder(
      portalBonusCampaignsApiMock.programs.uhrdGoalAchieved,
      portalBonusCampaignsApiMock.period
    );
  }
  if (
    props.campaignSlug === BONUS_CAMPAIGN_SLUG_AWANS &&
    mockAwansFirstMilestoneAchieved.value
  ) {
    return toGoalReminder(awansProgramMock.value, portalBonusCampaignsApiMock.period);
  }
  return campaign.value.goalReminder;
});

const awansWidgetData = computed(() => {
  if (!campaign.value || props.campaignSlug === BONUS_CAMPAIGN_SLUG_UHRD) {
    return campaign.value?.bonusDetail;
  }
  if (
    props.campaignSlug === BONUS_CAMPAIGN_SLUG_AWANS &&
    mockAwansFirstMilestoneAchieved.value
  ) {
    return toBonusAwansWidgetData(awansProgramMock.value);
  }
  return campaign.value.bonusDetail;
});

const uhrdWidgetData = computed(() => {
  if (!campaign.value || props.campaignSlug !== BONUS_CAMPAIGN_SLUG_UHRD) {
    return campaign.value?.bonusDetail;
  }
  if (!mockUhrdGoalAchieved.value) {
    return campaign.value.bonusDetail;
  }
  return toBonusUhrdWidgetData(
    portalBonusCampaignsApiMock.programs.uhrdGoalAchieved
  );
});

const pageI18nPrefix = computed(() => {
  if (props.campaignSlug === BONUS_CAMPAIGN_SLUG_GOALS) return "portalHome.bonusGoalsPage";
  if (props.campaignSlug === BONUS_CAMPAIGN_SLUG_AWANS) return "portalHome.bonusAwansPage";
  return "portalHome.bonusUhrdPage";
});

const chartTitleKey = computed(() => {
  if (props.campaignSlug === BONUS_CAMPAIGN_SLUG_GOALS) return "portalHome.bonusGoals.title";
  if (props.campaignSlug === BONUS_CAMPAIGN_SLUG_AWANS) return "portalHome.bonusAwans.title";
  return "portalHome.bonusUhrd.title";
});

const footerBundle = portalBonusCampaignFooter;
</script>

<style lang="scss">
@import "@/styles/portal-home/_portal-bonus-campaign-page.scss";
</style>

<style scoped lang="scss">
@import "@/styles/_portal_home.scss";

.portal-bonus-campaign-page__rules {
  margin-top: 8px;
}
</style>
